import {
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  Option,
  Select,
  Sheet,
  Stack,
  Tab,
  tabClasses,
  Table,
  TabList,
  TabPanel,
  Tabs,
  Typography,
} from "@mui/joy";
import PageTitleBox from "../../components/PageTitleBox";
import { Trans, useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import {
  FileDownloadOutlined,
  WbIncandescentOutlined,
} from "@mui/icons-material";
import { ItemService } from "../../services/item.service";
import { theme } from "../../components/Theme";
import {
  CloudArrowUpIcon,
  ExclamationCircleIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { useDropzone } from "react-dropzone";
import useContainer from "./useContainer";
import FailModal from "../../components/FailModal";
import LoadingModal from "../../components/LoadingModal/LoadingModal";
import { SuccessModal } from "../../components/SuccessModal";
import toast from "react-hot-toast";
import { unzipSync } from "fflate";
import { privateAxios } from "../../lib/api";
import { getActiveBusiness } from "../../store/session";
import {
  Autocomplete,
  Divider,
  TablePagination,
  TextField,
} from "@mui/material";
import { TrashIcon } from "@heroicons/react/24/solid";
import useEnhancedEffect from "@mui/material/utils/useEnhancedEffect";
import _ from "lodash";
import { useDebounce } from "../../lib/hooks";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { multiImageTableSchema } from "./schema/InventorySchema";
import { zodResolver } from "@hookform/resolvers/zod";
import { LoadingUI } from "./components/LoadingUI";
import CatalogLoader from "../../animations/catalog-loader.json";
import Lottie from "lottie-react";

interface Product {
  id: string;
  productName: string;
  sku: string;
}

const ExportImportInventory = () => {
  const { t } = useTranslation();

  const [selectedTab, setSelectedTab] = useState("export");

  const handleTabChange = (
    _event: React.SyntheticEvent | null,
    value: string | number | null
  ) => {
    if (typeof value === "string") {
      setSelectedTab(value);
    }
  };

  return (
    <Sheet
      sx={{
        backgroundColor: "transparent",
        overflowX: "hidden",
        display: "flex",
        flexDirection: "column",
        gap: "32px",
      }}
    >
      <Stack
        direction={"column"}
        sx={{
          width: "100%",
          gap: "32px",
        }}
      >
        <PageTitleBox
          title={t("catalog.inventory.import.title")}
          subtitle={t("catalog.inventory.import.subtitle")}
        />

        <Tabs
          aria-label="Basic tabs"
          value={selectedTab}
          onChange={handleTabChange}
          className="w-[90vw] md:w-[75vw]"
          sx={{
            backgroundColor: "transparent",
            marginTop: 4,
          }}
        >
          <TabList
            sx={{
              overflow: "auto",
              scrollSnapType: "x mandatory",
              "&::-webkit-scrollbar": { display: "none" },
              [`&& .${tabClasses.root}`]: {
                "&:hover": {
                  bgcolor: "transparent",
                },
                [`&.${tabClasses.selected}`]: {
                  "&::after": {
                    bgcolor: "primary.500",
                    borderTopLeftRadius: 3,
                    borderTopRightRadius: 3,
                    height: 2,
                  },
                  color: "primary.plainColor",
                },
                bgcolor: "transparent",
                flex: "initial",
                paddingY: 2,
              },
            }}
          >
            <Tab
              id="inventory-export-tab"
              value={"export"}
              key={"export"}
              sx={{
                fontWeight: "600",
                whiteSpace: "nowrap",
                minWidth: { xs: "50%", lg: 150 },
                flex: "none",
                scrollSnapAlign: "start",
                flexDirection: "column",
              }}
            >
              {t("catalog.inventory.import.tabs.exportTitle")}
            </Tab>
            <Tab
              id="inventory-import-tab"
              value={"import"}
              key={"import"}
              sx={{
                fontWeight: "600",
                whiteSpace: "nowrap",
                minWidth: { xs: "50%", lg: 150 },
                flex: "none",
                scrollSnapAlign: "start",
                flexDirection: "column",
              }}
            >
              {t("catalog.inventory.import.tabs.importTitle")}
            </Tab>
            <Tab
              id="upload-image-zip"
              value={"image-zip"}
              key={"image-zip"}
              sx={{
                fontWeight: "600",
                whiteSpace: "nowrap",
                minWidth: { xs: "50%", lg: 150 },
                flex: "none",
                scrollSnapAlign: "start",
                flexDirection: "column",
              }}
            >
              {t("catalog.inventory.import.tabs.zip.tab")}
            </Tab>
          </TabList>

          {/* default export tab */}
          <TabPanel value={"export"} key={"export"} sx={{ py: "32px" }}>
            <ExportPanel />
          </TabPanel>

          {/* default import tab */}
          <TabPanel value={"import"} key={"import"} sx={{ py: "32px" }}>
            <ImportPanel />
          </TabPanel>

          <TabPanel value={"image-zip"} key={"image-zip"} sx={{ py: "32px" }}>
            <ImageZip />
          </TabPanel>
        </Tabs>
      </Stack>
    </Sheet>
  );
};

const ExportPanel = () => {
  const { t } = useTranslation();

  const handleExcelDownload = async (_event: any) => {
    await ItemService.exportInventoryItems();
  };

  return (
    <Stack direction={"column"} gap={"16px"}>
      <Typography level="text-sm-medium">
        {t("catalog.inventory.import.tabs.export.exportPrompt")}
      </Typography>

      <Button
        id="inventory-export-button"
        variant="linkz-outlined-primary"
        startDecorator={<FileDownloadOutlined />}
        onClick={handleExcelDownload}
        sx={{
          width: "250px",
        }}
      >
        {t("catalog.inventory.import.tabs.export.exportInventoryBtn")}
      </Button>

      <Stack
        direction={"row"}
        sx={{
          gap: "8px",
          alignItems: "center",
        }}
      >
        <WbIncandescentOutlined color="primary" className="rotate-180" />
        <Typography level="text-xs-regular">
          {t("catalog.inventory.import.tabs.export.exportTip")}
        </Typography>
      </Stack>
    </Stack>
  );
};

const ImportPanel = () => {
  const { t } = useTranslation();
  const { onDrop, setExcelErrors, excelErrors, showAlert, alertMessage } =
    useContainer();

  const [showFailModal, setShowFailModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDrop: onDrop,
    accept: {
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
    },
    maxSize: 10 * 1024 * 1024,
    maxFiles: 1,
  });

  /**
   * will show fail modal based on excel upload error
   * @param fileRejections
   */
  useEffect(() => {
    if (fileRejections.length > 0) {
      const formattedErrors = fileRejections.map((reject) => {
        return {
          name: reject.file.name,
          errors: reject.errors,
        };
      });

      setIsLoading(false);
      setExcelErrors(formattedErrors);
      setShowFailModal(true);
    }
  }, [fileRejections]);

  /**
   * will show fail modal based on backend validation results
   * @param excelErrors
   */
  useEffect(() => {
    if (excelErrors.length > 0) {
      setIsLoading(false);
      setShowFailModal(true);
    }
  }, [excelErrors]);

  return (
    <Stack
      direction={{ xs: "column", lg: "row" }}
      gap={{ xs: "24px", lg: "32px" }}
    >
      <Box
        sx={{
          width: { xs: "100%", lg: "65%" },
          minHeight: { xs: "0", lg: "244px" },
          bgcolor: theme.palette.neutral[50],
          p: "32px",
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          justifyContent: "center",
          alignItems: "center",
          gap: "32px",
          borderWidth: "1px",
          borderColor: theme.palette.neutral[300],
          borderRadius: "8px",
        }}
        {...getRootProps()}
      >
        <img
          src="/bulk.png"
          width={100}
          height={100}
          className="object-contain"
        />

        <Stack
          direction={"column"}
          sx={{
            gap: "16px",
            alignItems: { xs: "center", lg: "baseline" },
          }}
        >
          <Stack direction={"column"} gap={"4px"}>
            <Typography level="text-md-bold">
              {t("catalog.inventory.import.tabs.import.importPrompt")}
            </Typography>

            <Typography
              level="text-xs-medium"
              textColor={theme.palette.neutral[600]}
            >
              {t("catalog.inventory.import.tabs.import.importSubtitle")}
            </Typography>
          </Stack>
          <Button
            id="inventory-import-button"
            // {...getRootProps({ className: "dropzone" })}
            variant="linkz-outlined-primary"
            startDecorator={<CloudArrowUpIcon width={20} height={20} />}
            sx={{
              width: "136px",
            }}
          >
            {t("commons.selectFileBtn")}
            <input {...getInputProps()} />
          </Button>
        </Stack>
      </Box>

      {/* tips */}
      <Box
        sx={{
          width: { xs: "100%", lg: "35%" },
          minHeight: { xs: "0", lg: "244px" },
          p: "24px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px",
          borderWidth: "1px",
          borderColor: theme.palette.neutral[300],
          borderRadius: "8px",
        }}
      >
        <Stack
          direction={"row"}
          sx={{
            gap: "8px",
            alignItems: "center",
            justifyContent: "left",
            textAlign: "left",
            width: "100%",
          }}
        >
          <WbIncandescentOutlined color="primary" className="rotate-180" />
          <Typography
            level="text-md-bold"
            textColor={theme.palette.neutral[600]}
          >
            {t("catalog.inventory.import.tabs.import.tip.title")}
          </Typography>
        </Stack>

        <Typography
          level="text-xs-medium"
          textColor={theme.palette.neutral[600]}
        >
          {t("catalog.inventory.import.tabs.import.tip.paragraph")}
        </Typography>

        <List
          marker="disc"
          size="sm"
          sx={{
            px: "24px",
          }}
        >
          {Array.from({ length: 4 }).map((_point, index) => (
            <ListItem sx={{ p: 0 }} key={index}>
              <Typography
                level="text-xs-medium"
                textColor={theme.palette.neutral[600]}
              >
                {t(`catalog.inventory.import.tabs.import.tip.${index + 1}`)}
              </Typography>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* modal */}
      <FailModal
        open={showFailModal}
        title="Failed Uploading File"
        content={
          <Box
            sx={{
              width: "100%",
              p: "16px",
              textAlign: "left",
              height: excelErrors.length > 1 ? "250px" : "auto",
              overflow: "auto",
            }}
            className="scrollbar-container"
          >
            <List marker="disc">
              {excelErrors.map(({ name, errors }) => {
                return name === "template" || name === "noData" ? (
                  <ListItem key={name}>
                    {t(`catalog.inventory.import.error.${name}`)}
                  </ListItem>
                ) : (
                  <ListItem key={name}>
                    {t("catalog.inventory.import.error.errorOnRow", {
                      row: name,
                    })}
                    :
                    <List marker="circle">
                      {errors.map((e: any) => (
                        <ListItem key={e.code}>{e.message}</ListItem>
                      ))}
                    </List>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        }
        onClose={() => {
          setShowFailModal(false);
        }}
      />
      <SuccessModal
        title={t("commons.successModalTitle")}
        content={alertMessage}
        open={showAlert}
      />
      <LoadingModal isLoading={isLoading} />
    </Stack>
  );
};

const ImageZip = () => {
  const { t } = useTranslation();
  const controllerRef = useRef<AbortController | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const allowedImageExt = [".jpg", ".jpeg", ".png", ".webp"];

  const business = getActiveBusiness();

  const [loading, setLoading] = useState(false);

  const [isUploading, setIsUploading] = useState(false);

  const [isSuccess, setIsSuccess] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const debounceValue = useDebounce(inputValue);
  const [options, setOptions] = useState<readonly Product[]>([]);
  const [value, setValue] = useState<Product | null>(null);
  const [debounceLoading, setDebounceLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [job, setJob] = useState(0);

  const [pagination, setPagination] = useState({
    page: 0,
    rowPerPage: 25,
  });

  const [file, setFile] = useState<File | null>(null);

  const [mapping, setMapping] = useState({
    total: 0,
    matched: 0,
    notMatch: 0,
    data: [],
  });

  const form = useForm<z.infer<typeof multiImageTableSchema>>({
    mode: "all",
    resolver: zodResolver(multiImageTableSchema),
    defaultValues: {
      meta_data: [],
    },
  });

  const { fields, remove } = useFieldArray({
    control: form.control,
    name: "meta_data",
  });

  const startIndex = pagination.page * pagination.rowPerPage;
  const paginatedFields = fields.slice(
    startIndex,
    startIndex + pagination.rowPerPage
  );

  const [__, setImages] =
    useState<{ sku: string; name: string; blobUrl: string }[]>();

  const unzipAndMapData = async (file: File) => {
    const buffer = await file.arrayBuffer();
    const unzipped = unzipSync(new Uint8Array(buffer));

    const images = [];

    for (const [fileName, content] of Object.entries(unzipped)) {
      // Skip Apple/macOS junk
      if (
        fileName.startsWith("__macosx/") ||
        fileName.split("/").some((part) => part.startsWith("._"))
      ) {
        continue;
      }

      const pathParts = fileName.split("/");
      const baseFileName = pathParts[pathParts.length - 1];

      const ext = allowedImageExt.find((ext) => baseFileName.endsWith(ext));
      if (!ext) continue;

      const sku = baseFileName.replace(/-\d+(?=\.\w+$)|\.\w+$/g, "");

      const blob = new Blob([content]);
      const blobUrl = URL.createObjectURL(blob);

      images.push({ sku, name: baseFileName, blobUrl });
    }

    const skus = images.map((img) => img.sku);
    const data = await fetchMatchingSKU(skus);
    return { images, data };
  };

  const fetchMatchingSKU = async (skus: string[]) => {
    const { data } = await privateAxios.post(`/inventory/images-match`, {
      skus,
      business_id: business?.id,
    });
    return data;
  };

  const onDrop = async (acceptedFiles: File[]) => {
    if (acceptedFiles.length == 1) {
      setLoading(true);
      setFile(acceptedFiles[0]);

      const { images, data } = await unzipAndMapData(acceptedFiles[0]);
      setImages(images);
      const matched = data.filter(
        (d: { product: {} | null }) => d.product != null
      ).length;

      setMapping({
        total: images.length,
        matched,
        notMatch: Math.max(0, images.length - matched),
        data,
      });
      const formInitialValue = images.map((image) => {
        const matchingProduct = data.find(
          (product: { sku: string; product: Product } | null) =>
            product?.sku == image.sku
        );
        const skuGroup = images.filter((product) => product?.sku == image.sku);
        console.log("MATCHING PRODUCT", matchingProduct);

        return {
          sku: image.sku,
          fileName: image.name,
          product: matchingProduct.product
            ? {
                id: matchingProduct.product.id,
                productName: matchingProduct.product.productName,
                sku: matchingProduct.sku,
              }
            : { id: "", productName: "", sku: "" },
          imageType: getLabelFromFilename(
            image.name,
            skuGroup,
            !!matchingProduct.product
          ),
          blobUrl: image.blobUrl,
        };
      });

      const formInitialValueSorted = _.sortBy(formInitialValue, (item) =>
        (item.product.id === "" && item.product.sku === "") ||
        item.imageType == null ||
        item.imageType == ""
          ? 0
          : 1
      );

      setOptions(
        formInitialValue.map(({ product }) => ({
          id: product.id,
          sku: product.sku,
          productName: product.productName,
        }))
      );
      form.setValue("meta_data", formInitialValueSorted);
      await form.trigger("meta_data");
      setLoading(false);
    }
  };

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDrop: onDrop,
    accept: {
      "application/zip": [".zip"],
    },
    maxSize: 50 * 1024 * 1024,
    maxFiles: 1,
  });

  const getLabelFromFilename = (
    filename: string,
    skuGroup: { sku: string; name: string }[],
    isMatch: boolean
  ): string => {
    if (!isMatch) return "";
    const labelOptions = ["COVER", "IMAGE-1", "IMAGE-2", "IMAGE-3"];
    const availableLabels = labelOptions.slice(0, Math.min(skuGroup.length, 4));

    const assigned: Record<string, string> = {};
    const usedLabels = new Set<string>();

    // 1. Assign labels based on file name pattern like -1.jpg => IMAGE-0
    skuGroup.forEach((img) => {
      const match = img.name.match(/-(\d+)\.(jpg|jpeg|png|webp)$/i);

      if (match) {
        const num = parseInt(match[1], 10);
        const index = num - 1;
        const label = labelOptions[index];

        if (
          label &&
          availableLabels.includes(label) &&
          !usedLabels.has(label)
        ) {
          assigned[img.name] = label;
          usedLabels.add(label);
        }
      } else if (
        new RegExp(`^${img.sku}\\.(jpg|jpeg|png|webp)$`, "i").test(img.name)
      ) {
        if (!usedLabels.has("COVER") && availableLabels.includes("COVER")) {
          assigned[img.name] = "COVER";
          usedLabels.add("COVER");
        }
      }
    });

    // 2. Fallback: assign remaining unused labels in order
    // const fallbackPool = availableLabels.filter(
    //   (label) => !usedLabels.has(label)
    // );
    // let fallbackIndex = 0;

    skuGroup.forEach((img) => {
      if (!assigned[img.name]) {
        assigned[img.name] = "";
        // if (fallbackIndex < fallbackPool.length) {
        //   const label = fallbackPool[fallbackIndex];
        //   assigned[img.name] = label;
        //   usedLabels.add(label);
        //   fallbackIndex++;
        // } else {
        //   assigned[img.name] = ""; // Nothing left — assign empty
        // }
      }
    });

    return assigned[filename] ?? "";
  };

  const fetchWithDebounce = _.debounce(
    async (
      request: { input: string },
      callback: (results?: readonly Product[]) => void
    ) => {
      try {
        setDebounceLoading(true);
        const { data } = await privateAxios.get(
          `/inventory/images/search-product?search=${request.input}&business_id=${business?.id}`
        );

        const products = data.data.map((product: Product) => ({
          id: product.id,
          sku: product.sku,
          productName: product.productName,
        }));

        callback(products);
        setDebounceLoading(false);
      } catch (err: any) {
        throw err;
      }
    },
    600
  );

  useEffect(() => {
    if (fileRejections.length > 0) {
      const error = fileRejections.map((reject) => reject.errors).flat()[0];

      if (error.code == "file-invalid-type") {
        toast.error("Only Zip file are allowed");
        return;
      }
      if (error.code == "too-many-files") {
        toast.error(
          "Multiple Zip are not supported yet, upload only one zip file"
        );
        return;
      }

      if (error.code == "file-too-large") {
        toast.error("Zip file size is too big, must be under 50 MB");
        return;
      }

      toast.error(`${error.message}`);
    }
  }, [fileRejections]);

  useEnhancedEffect(() => {
    setDebounceLoading(true);
    if (debounceValue === "") {
      setOptions(value ? [value] : []);
      setDebounceLoading(false);
      return undefined;
    }

    let active = true;

    fetchWithDebounce({ input: debounceValue }, (results) => {
      if (!active) {
        return;
      }

      let newOptions: readonly Product[] = [];

      if (results && results.length) {
        newOptions = results;

        if (value) {
          newOptions = [
            value,
            ...results.filter((result) => result.id !== value.id),
          ];
        }
      } else if (value) {
        newOptions = [value];
      }
      setOptions(newOptions);
    });

    return () => {
      active = false;
      fetchWithDebounce.cancel();
    };
  }, [debounceValue]);

  const handleSubmit = async (data: z.infer<typeof multiImageTableSchema>) => {
    const groups = _.groupBy(data.meta_data, "product.sku");

    const payload = Object.entries(groups).map(([sku, items]) => ({
      sku,
      gallery: items.map((item) => ({
        fileName:
          item.fileName.substring(0, item.fileName.lastIndexOf(".")) ||
          item.fileName,
        imageType: item.imageType,
      })),
    }));

    const formData = new FormData();
    if (file) {
      formData.append("file", file);
    }
    formData.append("meta_data", JSON.stringify(payload));

    try {
      setIsUploading(true);
      setIsSuccess(false);
      const { data: response } = await privateAxios.post(
        `/inventory/images?business_id=${business?.id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.success) {
        setJob(response.data.jobId);
      } else {
        setIsUploading(false);
        setIsSuccess(true);
        toast(
          "Your uploading progress is running in background, check inventory later"
        );
      }
    } catch (e) {
      setIsUploading(false);
      setIsSuccess(false);
    }
  };

  const updateProgress = async () => {
    try {
      const { data } = await privateAxios.get(`/inventory/images/job/${job}`);
      setProgress(data.data.progress);
      if (data.data.progress >= 100) {
        setTimeout(() => {
          setIsUploading(false);
          setIsSuccess(true);
        }, 2000);
      }
    } catch (e) {
      console.log(e);
      toast(
        "Your uploading progress is running in background, check inventory later"
      );
      setIsUploading(false);
      setIsSuccess(true);
    }
  };

  useEffect(() => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }

    const controller = new AbortController();
    controllerRef.current = controller;

    if (isUploading && job != 0 && progress < 100) {
      intervalRef.current = setInterval(updateProgress, 3000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
    };
  }, [isUploading, job]);

  useEffect(() => {
    if (isSuccess) {
      setTimeout(() => {
        location.href = "/client/inventory";
      }, 2000);
    }
  }, [isSuccess]);

  // useEffect(() => {
  //   const formError = (form.formState.errors.meta_data ?? []) as unknown[];
  //   const notMatch = formError?.filter((e) => e != null).length;
  //   setNoMatces(notMatch);
  // }, [form.formState.errors]);

  if (loading) {
    return (
      <div className="w-full">
        <Stack
          border={1}
          borderColor="#cecece"
          sx={{
            flexDirection: "row",
            justifyContent: "space-around",
            alignItems: "center",
            marginBottom: 4,
          }}
        >
          <Box textAlign="center" py={2}>
            <Typography sx={{ fontSize: { xs: 12, md: 18 } }}>
              Total Uploaded Images
            </Typography>
            <Typography fontWeight="bold" fontSize={20}>
              -
            </Typography>
          </Box>
          <Divider orientation="vertical" sx={{ height: 90 }} />
          <Box textAlign="center" py={2}>
            <Typography sx={{ fontSize: { xs: 12, md: 18 } }}>
              Total Matched Images
            </Typography>
            <Typography fontWeight="bold" fontSize={20} color="primary">
              -
            </Typography>
          </Box>
          <Divider orientation="vertical" sx={{ height: 90 }} />
          <Box textAlign="center" py={2}>
            <Typography sx={{ fontSize: { xs: 12, md: 18 } }}>
              Total Unmatched Images
            </Typography>
            <Typography
              fontWeight="bold"
              fontSize={20}
              color={mapping.notMatch > 0 ? "danger" : "primary"}
            >
              -
            </Typography>
          </Box>
        </Stack>
        <Stack
          width="100%"
          minHeight={400}
          sx={{
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: "300px",
              height: "250px",
              alignSelf: "center",
            }}
          >
            <Lottie animationData={CatalogLoader} loop={true} autoplay={true} />
          </Box>

          <div>
            <Typography level="h4">
              {t("catalog.inventory.import.tabs.zip.loading.title")}
            </Typography>
            <Typography level="body-md">
              {t("catalog.inventory.import.tabs.zip.loading.description")}
            </Typography>
          </div>
        </Stack>
      </div>
    );
  }

  if (fields && fields.length > 0 && !loading) {
    const formError = (form.formState.errors.meta_data ?? []) as unknown[];
    const total = fields.length;
    const notMatch = formError?.filter((e) => e != null).length;
    const matched = Math.max(0, total - notMatch);

    return (
      <form className="w-full" onSubmit={form.handleSubmit(handleSubmit)}>
        <Stack
          border={1}
          borderColor="#cecece"
          sx={{
            flexDirection: "row",
            justifyContent: "space-around",
            alignItems: "center",
            marginBottom: 4,
          }}
        >
          <Box textAlign="center" py={2}>
            <Typography sx={{ fontSize: { xs: 12, md: 18 } }}>
              Total Uploaded Images
            </Typography>
            <Typography fontWeight="bold" fontSize={20}>
              {total}
            </Typography>
          </Box>
          <Divider orientation="vertical" sx={{ height: 90 }} />
          <Box textAlign="center" py={2}>
            <Typography sx={{ fontSize: { xs: 12, md: 18 } }}>
              Total Matched Images
            </Typography>
            <Typography fontWeight="bold" fontSize={20} color="primary">
              {matched}
            </Typography>
          </Box>
          <Divider orientation="vertical" sx={{ height: 90 }} />
          <Box textAlign="center" py={2}>
            <Typography sx={{ fontSize: { xs: 12, md: 18 } }}>
              Total Unmatched Images
            </Typography>
            <Typography
              fontWeight="bold"
              fontSize={20}
              color={notMatch > 0 ? "danger" : "primary"}
            >
              {notMatch}
            </Typography>
          </Box>
        </Stack>
        <Sheet
          variant="outlined"
          sx={{
            overflow: "auto",
          }}
        >
          <Table
            stickyHeader
            sx={{
              "& tr > *:last-child": {
                position: "sticky",
                right: 0,
                bgcolor: "#fff",
              },
              "--Table-headerUnderlineThickness": "1px",

              maxWidth: { lg: "100%", xs: "100%" },
            }}
          >
            <thead>
              <tr>
                <th style={{ width: "50px" }}>No.</th>
                <th style={{ width: "250px" }}>Cover Image</th>
                <th style={{ width: "200px" }}>Product SKU</th>
                <th style={{ width: "400px" }}>Product Name</th>
                <th style={{ width: "250px" }}>Image Order</th>
                <th style={{ width: "100px" }}>Status</th>
                <th style={{ width: "50px" }}>Remove</th>
              </tr>
            </thead>
            <tbody>
              {paginatedFields.map((image, index) => {
                console.log("IMAGES", paginatedFields);
                const actualIndex = startIndex + index;
                const liveChange = form.watch(
                  `meta_data.${actualIndex}.product`
                );

                const imageType = form.watch(
                  `meta_data.${actualIndex}.imageType`
                );

                return (
                  <tr key={fields[actualIndex].id}>
                    <td>{actualIndex + 1}.</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <img
                          src={image.blobUrl}
                          className="w-14 h-14 rounded-lg object-cover"
                        />
                        <Typography fontWeight={500} fontSize={14}>
                          {image.fileName}
                        </Typography>
                      </div>
                    </td>
                    <td
                      className={`${
                        liveChange?.sku ? "text-black" : "text-danger-500"
                      }`}
                    >
                      {liveChange?.sku ? liveChange.sku : "No SKU Found"}
                    </td>
                    <td>
                      <Controller
                        name={`meta_data.${actualIndex}.product`}
                        control={form.control}
                        render={({ field }) => (
                          <Autocomplete
                            {...field}
                            options={
                              debounceLoading
                                ? [
                                    {
                                      id: "loading",
                                      productName: "Loading...",
                                      sku: "",
                                    },
                                  ]
                                : options
                            }
                            autoComplete
                            includeInputInList
                            filterSelectedOptions
                            filterOptions={(x) => x}
                            loadingText="Loading..."
                            loading={debounceLoading}
                            isOptionEqualToValue={(option, value) =>
                              option.id === value.id
                            }
                            getOptionLabel={(option) =>
                              typeof option === "string"
                                ? option
                                : option.productName
                            }
                            noOptionsText="No Match"
                            value={field.value}
                            renderInput={(params) => (
                              <TextField {...params} size="small" />
                            )}
                            onChange={async (
                              _: any,
                              newValue: Product | null
                            ) => {
                              field.onChange(newValue);
                              setValue(newValue);
                            }}
                            onInputChange={(_, newInputValue) => {
                              setInputValue(newInputValue);
                            }}
                            renderOption={(props, option) => {
                              const { key, ...optionProps } = props;

                              if (option.id === "loading") {
                                return (
                                  <li
                                    key={key}
                                    {...optionProps}
                                    className="p-2 mx-2 rounded-lg flex items-center justify-center text-gray-500"
                                  >
                                    <Typography fontSize={14}>
                                      Please wait...
                                    </Typography>
                                  </li>
                                );
                              }

                              return (
                                <li
                                  key={key}
                                  {...optionProps}
                                  className="p-2 hover:bg-slate-100 mx-2 cursor-pointer rounded-lg flex  items-center justify-between"
                                >
                                  <div>
                                    <Typography fontWeight={500} fontSize={14}>
                                      {option.sku}
                                    </Typography>
                                    <Typography fontWeight={500} fontSize={14}>
                                      {option.productName}
                                    </Typography>
                                  </div>
                                  <PlusIcon className="size-4" />
                                </li>
                              );
                            }}
                          />
                        )}
                      />
                    </td>
                    <td>
                      <Controller
                        name={`meta_data.${actualIndex}.imageType`}
                        control={form.control}
                        render={({ field }) => {
                          const beforeChangeValue = field.value;
                          const currentSKU = form.getValues(
                            `meta_data.${actualIndex}.product`
                          );
                          // switch comparison to product id
                          const skuGroup = form
                            .watch("meta_data")
                            .filter(
                              (productRow) =>
                                productRow.product?.id == currentSKU?.id
                            );
                          const options: string[] = [];

                          console.log("FORMVALUES", form.getValues());

                          skuGroup.slice(0, 4).forEach((_, i) => {
                            if (i == 0) {
                              options.push("COVER");
                            } else {
                              options.push(`IMAGE-${i}`);
                            }
                          });

                          return (
                            <Select
                              {...field}
                              size="sm"
                              placeholder="Choose One"
                              onChange={async (_: any, v) => {
                                const toSwapRow = form
                                  .getValues("meta_data")
                                  .findIndex(
                                    (product) =>
                                      product.imageType == v &&
                                      product.product?.id ==
                                        form.getValues(
                                          `meta_data.${actualIndex}.product`
                                        )?.id
                                  );

                                field.onChange(v);
                                if (toSwapRow != -1) {
                                  form.setValue(
                                    `meta_data.${toSwapRow}.imageType`,
                                    beforeChangeValue
                                  );
                                }

                                setTimeout(() => {
                                  form.trigger();
                                }, 100);
                              }}
                            >
                              {options.map((o, i) => (
                                <Option key={i} value={o}>
                                  {o.replace("-", " ")}
                                </Option>
                              ))}
                            </Select>
                          );
                        }}
                      />
                    </td>
                    <td>
                      <Chip
                        color={
                          liveChange?.id && imageType ? "success" : "danger"
                        }
                        variant="outlined"
                        size="sm"
                        sx={{
                          "--Chip-radius": "8px",
                        }}
                      >
                        {liveChange?.id && imageType ? "Matched" : "Not Match"}
                      </Chip>
                    </td>
                    <td>
                      <IconButton
                        color="danger"
                        onClick={() => remove(actualIndex)}
                      >
                        <TrashIcon className="size-6 text-danger-500" />
                      </IconButton>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Sheet>
        <TablePagination
          component="div"
          count={fields.length}
          page={pagination.page}
          rowsPerPage={pagination.rowPerPage}
          onPageChange={(_, page) =>
            setPagination(() => ({ ...pagination, page: page }))
          }
          onRowsPerPageChange={(e) => {
            setPagination(() => ({
              ...pagination,
              rowPerPage: +e.target.value,
            }));
          }}
        />
        <LoadingUI {...{ isUploading, setIsUploading, progress }} />

        <SuccessModal
          open={isSuccess}
          title="Success"
          content={`You've successfully updated images for ${mapping.total} items.`}
        />
        <Divider className="my-4" />
        <Stack
          gap={1}
          direction="row"
          sx={{
            justifyContent: "flex-end",
          }}
        >
          <Button
            size="sm"
            variant="linkz-outlined-danger"
            type="button"
            onClick={() => window.history.back()}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={notMatch > 0}
            size="sm"
            variant={
              notMatch === 0 ? "linkz-outlined-primary" : "outlined-gray"
            }
          >
            Update Change
          </Button>
        </Stack>
      </form>
    );
  }

  return (
    <Stack
      direction={{ xs: "column", lg: "row" }}
      gap={{ xs: "24px", lg: "32px" }}
    >
      <Box
        sx={{
          width: { xs: "100%", lg: "65%" },
          flex: 2,
          minHeight: { xs: "0", lg: "480px" },
          bgcolor: theme.palette.neutral[50],
          p: "32px",
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          justifyContent: "center",
          alignItems: "center",
          gap: "32px",
          borderWidth: "1px",
          borderColor: theme.palette.neutral[300],
          borderRadius: "8px",
        }}
        {...getRootProps()}
      >
        <img
          src="/zip-icon.png"
          width={100}
          height={100}
          className="object-contain"
        />

        <Stack
          direction={"column"}
          sx={{
            gap: "16px",
            alignItems: { xs: "center", lg: "baseline" },
          }}
        >
          <Stack direction={"column"} gap={"4px"}>
            <Typography level="text-lg-bold">
              {t("catalog.inventory.import.tabs.zip.uploadTitle")}
            </Typography>

            <Typography
              level="text-sm-medium"
              textColor={theme.palette.neutral[600]}
            >
              {t("catalog.inventory.import.tabs.zip.uploadSize")}
            </Typography>
          </Stack>
          <Button
            id="inventory-import-button"
            variant="linkz-outlined-primary"
            startDecorator={<CloudArrowUpIcon width={20} height={20} />}
            sx={{
              width: "136px",
            }}
          >
            {t("commons.selectFileBtn")}
            <input {...getInputProps()} />
          </Button>
        </Stack>
      </Box>

      <Stack gap={2} flex={1}>
        <Box
          sx={{
            // minHeight: { xs: "0", lg: "240px" },
            height: "100%",
            p: "24px",
            display: "flex",
            flexDirection: "column",
            borderWidth: "1px",
            borderColor: theme.palette.neutral[300],
            borderRadius: "8px",
            gap: "16px",
          }}
        >
          <Stack
            direction={"row"}
            sx={{
              gap: "8px",
              alignItems: "center",
              justifyContent: "left",
              textAlign: "left",
              width: "100%",
            }}
          >
            <ExclamationCircleIcon
              color="primary"
              className="size-6"
              style={{
                color: theme.palette.primary[500],
              }}
            />
            <Typography
              level="text-md-bold"
              textColor={theme.palette.neutral[600]}
            >
              {t("catalog.inventory.import.tabs.zip.top-box.label")}
            </Typography>
          </Stack>

          <Typography
            level="text-xs-medium"
            textColor={theme.palette.neutral[600]}
          >
            {t("catalog.inventory.import.tabs.zip.top-box.description")}
          </Typography>

          <List
            marker="disc"
            size="sm"
            sx={{
              px: "24px",
              gap: "16px",
            }}
          >
            <ListItem sx={{ p: 0 }}>
              <Typography
                level="text-xs-medium"
                textColor={theme.palette.neutral[600]}
                whiteSpace="pre-wrap"
              >
                <Trans
                  i18nKey="catalog.inventory.import.tabs.zip.top-box.list-1"
                  components={{
                    highlight: <Typography level="text-xs-semibold" />,
                  }}
                />
                {/* {t("catalog.inventory.import.tabs.zip.top-box.list-1")} */}
              </Typography>
            </ListItem>
            <ListItem sx={{ p: 0 }}>
              <Typography
                level="text-xs-medium"
                textColor={theme.palette.neutral[600]}
              >
                {t("catalog.inventory.import.tabs.zip.top-box.list-2")}
              </Typography>
            </ListItem>
            <ListItem sx={{ p: 0 }}>
              <Typography
                level="text-xs-medium"
                textColor={theme.palette.neutral[600]}
              >
                <Trans
                  i18nKey="catalog.inventory.import.tabs.zip.top-box.list-3"
                  components={{
                    highlight: <Typography level="text-xs-semibold" />,
                    br: <br />,
                  }}
                />
                {/* {t("catalog.inventory.import.tabs.zip.top-box.list-3")} */}
              </Typography>
            </ListItem>
            <ListItem sx={{ p: 0 }}>
              <Typography
                level="text-xs-medium"
                textColor={theme.palette.neutral[600]}
              >
                {t("catalog.inventory.import.tabs.zip.top-box.list-4")}
              </Typography>
            </ListItem>
            <ListItem sx={{ p: 0 }}>
              <Typography
                level="text-xs-medium"
                textColor={theme.palette.neutral[600]}
              >
                {t("catalog.inventory.import.tabs.zip.top-box.list-5")}
              </Typography>
            </ListItem>
            <ListItem sx={{ p: 0 }}>
              <Typography
                level="text-xs-medium"
                textColor={theme.palette.neutral[600]}
              >
                <Trans
                  i18nKey="catalog.inventory.import.tabs.zip.top-box.list-6"
                  components={{
                    highlight: <Typography level="text-xs-semibold" />,
                  }}
                />
              </Typography>
            </ListItem>
          </List>
        </Box>

        {/* <Box
          sx={{
            minHeight: { xs: "0", lg: "240px" },
            p: "24px",
            display: "flex",
            flexDirection: "column",
            borderWidth: "1px",
            borderColor: theme.palette.neutral[300],
            borderRadius: "8px",
          }}
        >
          <Stack
            direction={"row"}
            sx={{
              gap: "4px",
              alignItems: "center",
              justifyContent: "left",
              textAlign: "left",
              width: "100%",
            }}
          >
            <WbIncandescentOutlined color="primary" className="rotate-180" />
            <Typography
              level="text-md-bold"
              textColor={theme.palette.neutral[600]}
            >
              {t("catalog.inventory.import.tabs.zip.bottom-box.label")}
            </Typography>
          </Stack>

          <List
            marker="number"
            size="sm"
            sx={{
              px: "24px",
            }}
          >
            <ListItem sx={{ p: 0 }}>
              <Typography
                level="text-xs-medium"
                textColor={theme.palette.neutral[600]}
                whiteSpace="pre-wrap"
              >
                {t("catalog.inventory.import.tabs.zip.bottom-box.list-1")}
              </Typography>
            </ListItem>
            <ListItem sx={{ p: 0 }}>
              <Typography
                level="text-xs-medium"
                textColor={theme.palette.neutral[600]}
              >
                {t("catalog.inventory.import.tabs.zip.bottom-box.list-2")}
              </Typography>
            </ListItem>
            <ListItem sx={{ p: 0 }}>
              <Typography
                level="text-xs-medium"
                textColor={theme.palette.neutral[600]}
              >
                {t("catalog.inventory.import.tabs.zip.bottom-box.list-3")}
              </Typography>
            </ListItem>
          </List>
        </Box> */}
      </Stack>
    </Stack>
  );
};

export default ExportImportInventory;
